import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { UploadQuotaExceededDialog } from "@/components/common/UploadQuotaExceededDialog";
import type { UploadQuotaExceededEventDetail } from "@/lib/uploadQuotaEvents";
import { setUploadQuotaExceededListener } from "@/lib/uploadQuotaEvents";

type UploadQuotaDialogContextValue = {
  showUploadQuotaExceeded: (detail: UploadQuotaExceededEventDetail) => void;
};

const UploadQuotaDialogContext =
  createContext<UploadQuotaDialogContextValue | null>(null);

type UploadQuotaDialogProviderProps = {
  children: ReactNode;
};

export const UploadQuotaDialogProvider = ({
  children,
}: UploadQuotaDialogProviderProps) => {
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState<UploadQuotaExceededEventDetail | null>(
    null,
  );

  const showUploadQuotaExceeded = useCallback(
    (nextDetail: UploadQuotaExceededEventDetail) => {
      setDetail(nextDetail);
      setOpen(true);
    },
    [],
  );

  useEffect(() => {
    return setUploadQuotaExceededListener(showUploadQuotaExceeded);
  }, [showUploadQuotaExceeded]);

  const contextValue = useMemo<UploadQuotaDialogContextValue>(
    () => ({
      showUploadQuotaExceeded,
    }),
    [showUploadQuotaExceeded],
  );

  return (
    <UploadQuotaDialogContext.Provider value={contextValue}>
      {children}
      <UploadQuotaExceededDialog
        open={open}
        onOpenChange={setOpen}
        detail={detail}
      />
    </UploadQuotaDialogContext.Provider>
  );
};

export const useUploadQuotaDialog = (): UploadQuotaDialogContextValue => {
  const context = useContext(UploadQuotaDialogContext);
  if (!context) {
    throw new Error(
      "useUploadQuotaDialog must be used within UploadQuotaDialogProvider",
    );
  }
  return context;
};
